import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPostBySlug, getAssetUrl } from '../lib/directus';
import { Post } from '../lib/directus';
import { Calendar, Tag, Folder } from 'lucide-react';

const SITE_DOMAIN = import.meta.env.VITE_SITE_DOMAIN || 'o4ki.online';

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;
      try {
        setLoading(true);
        const data = await getPostBySlug(slug, SITE_DOMAIN);
        if (data) {
          setPost(data);
        } else {
          setError('Статья не найдена');
        }
      } catch (err) {
        console.error('Ошибка загрузки статьи:', err);
        setError('Не удалось загрузить статью');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          <div className="h-64 bg-gray-200 rounded mb-8"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h1 className="text-3xl font-bold text-red-600 mb-4">Ошибка</h1>
        <p className="text-gray-700">{error || 'Статья не найдена'}</p>
        <a href="/blog" className="mt-6 inline-block text-burgundy hover:underline">
          ← Вернуться к списку статей
        </a>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <article className="bg-white rounded-lg shadow-lg p-8 max-w-4xl mx-auto">
        {post.cover && (
          <img
            src={getAssetUrl(post.cover) || undefined}
            alt={post.title}
            className="w-full h-auto rounded-lg mb-8"
          />
        )}
        <h1 className="text-4xl font-bold text-burgundy mb-4">{post.title}</h1>
        <p className="text-gray-600 text-lg mb-8">{post.excerpt}</p>
        <div className="prose prose-lg max-w-none">
          <div dangerouslySetInnerHTML={{ __html: post.content }} />
        </div>
        <div className="mt-8 pt-8 border-t border-gray-200 flex flex-wrap items-center text-sm text-gray-600">
          <div className="flex items-center mr-6">
            <Calendar className="w-4 h-4 mr-2" />
            <span>{new Date(post.publish_date).toLocaleDateString('ru-RU')}</span>
          </div>
          {post.tags && post.tags.length > 0 && (
            <div className="flex items-center mr-6">
              <Tag className="w-4 h-4 mr-2" />
              <span>{post.tags.join(', ')}</span>
            </div>
          )}
          {post.categories && post.categories.length > 0 && (
            <div className="flex items-center">
              <Folder className="w-4 h-4 mr-2" />
              <span>
                {post.categories.map(c => c.categories_id.name).join(', ')}
              </span>
            </div>
          )}
        </div>
        <div className="mt-8">
          <a href="/blog" className="text-burgundy hover:underline">
            ← Вернуться к списку статей
          </a>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;